import { useCallback, useSyncExternalStore } from 'react';
import { defaultBrandPresetId, isBrandPresetId, type LuminosityMode } from './panelBrand';

export type ThemeMode = LuminosityMode;

type ThemePreference = {
  luminosityMode: LuminosityMode;
  brandPresetId: string;
};

const STORAGE_KEY = 'palacete-theme';

function resolveIsDark(mode: LuminosityMode): boolean {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStoredPreference(): ThemePreference {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return { luminosityMode: 'system', brandPresetId: defaultBrandPresetId };
  }

  try {
    const parsed = JSON.parse(raw) as Partial<ThemePreference>;
    const luminosityMode =
      parsed.luminosityMode === 'light' || parsed.luminosityMode === 'dark' || parsed.luminosityMode === 'system'
        ? parsed.luminosityMode
        : 'system';
    const brandPresetId = isBrandPresetId(parsed.brandPresetId) ? parsed.brandPresetId : defaultBrandPresetId;
    return { luminosityMode, brandPresetId };
  } catch {
    const legacyMode = raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
    return { luminosityMode: legacyMode, brandPresetId: defaultBrandPresetId };
  }
}

function applyTheme(preference: ThemePreference) {
  const isDark = resolveIsDark(preference.luminosityMode);
  const root = document.documentElement;
  root.classList.toggle('dark', isDark);
  root.dataset.brandPreset = preference.brandPresetId;
  root.dataset.luminosityMode = preference.luminosityMode;
  root.dataset.luminosityResolved = isDark ? 'dark' : 'light';
  root.style.colorScheme = isDark ? 'dark' : 'light';
}

/**
 * Estado de tema compartido a nivel de módulo. `useTheme` puede llamarse desde
 * varios componentes a la vez (header, switcher de preset, etc.) y todos deben
 * leer y escribir la MISMA preferencia. Si cada consumidor tuviera su propio
 * `useState`, una instancia con un valor viejo podría reaplicar `applyTheme` y
 * pisar el preset/luminosidad que otra acaba de fijar.
 */
let current: ThemePreference = readStoredPreference();
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function setPreference(next: ThemePreference) {
  current = next;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  applyTheme(next);
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ThemePreference {
  return current;
}

// Aplica el estado inicial y mantiene `system` sincronizado con el SO.
if (typeof window !== 'undefined') {
  applyTheme(current);
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    if (current.luminosityMode !== 'system') return;
    applyTheme(current);
    emit();
  });
}

export function useTheme() {
  const preference = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setTheme = useCallback((next: LuminosityMode) => {
    setPreference({ ...current, luminosityMode: next });
  }, []);

  const setBrandPreset = useCallback((next: string) => {
    if (!isBrandPresetId(next)) return;
    setPreference({ ...current, brandPresetId: next });
  }, []);

  const isDark = resolveIsDark(preference.luminosityMode);

  return {
    mode: preference.luminosityMode,
    luminosityMode: preference.luminosityMode,
    brandPresetId: preference.brandPresetId,
    setTheme,
    setLuminosityMode: setTheme,
    setBrandPreset,
    isDark,
  };
}
